import React, { Fragment, useEffect, useState } from "react";
import Breadcrumb from "../common/breadcrumb";
import {
  Settings, Star, Save, Edit, Trash2, Sliders, Grid, Package, Film, Key,
  Monitor, Layout, Airplay, ShoppingBag, Lock,
} from "react-feather";
import {
  Button, Container, Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import {
  getScreenById, updateScreen, reissueScreenApiKey, getScreenCategories, setScreenCategories,
  getScreenProducts, setScreenProducts, getScreenPromotions,
  createScreenPromotion, updateScreenPromotion, deleteScreenPromotion,
  getSpanMachineList,
} from "../../utils/apiCalls";
import { useParams } from "react-router-dom";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Loader as RLoader } from "../reporting/ReportingShared";

const TABS = [
  { key: "general", label: "General", Icon: Sliders },
  { key: "categories", label: "Categories", Icon: Grid },
  { key: "products", label: "Products", Icon: Package },
  { key: "promotions", label: "Promotions", Icon: Film },
];

// ---- General tab building blocks ----
// Declared outside the page component on purpose: a component defined inside render is a new type
// on every render, so React would remount it and every input in it would lose focus per keystroke.

const hintStyle = { fontSize: 12, color: C.textLight, marginTop: 6, lineHeight: 1.45 };
const warnStyle = { ...hintStyle, color: C.orange };

/** One titled group of settings. */
const Section = ({ icon: Icon, title, subtitle, children, aside }) => (
  <div
    style={{
      border: `1px solid ${C.border}`,
      borderRadius: 12,
      background: C.white,
      padding: 18,
      display: "flex",
      flexDirection: "column",
      gap: 14,
    }}
  >
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: C.coralLight,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={16} color={C.coral} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: C.textLight, marginTop: 2 }}>{subtitle}</div>}
      </div>
      {aside}
    </div>
    {children}
  </div>
);

/** An on/off switch with a label and an optional hint underneath. */
const Switch = ({ checked, onChange, label, hint, disabled }) => (
  <label
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.55 : 1,
      margin: 0,
    }}
  >
    <input
      type="checkbox"
      checked={!!checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
    />
    <span
      aria-hidden
      style={{
        width: 36,
        height: 20,
        borderRadius: 10,
        background: checked ? C.coral : C.border,
        position: "relative",
        flexShrink: 0,
        marginTop: 1,
        transition: "background 0.15s",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: checked ? 18 : 2,
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: C.white,
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          transition: "left 0.15s",
        }}
      />
    </span>
    <span style={{ minWidth: 0 }}>
      <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.text }}>{label}</span>
      {hint && <span style={{ display: "block", ...hintStyle, marginTop: 2 }}>{hint}</span>}
    </span>
  </label>
);

/** Two or more mutually exclusive choices, each with a one-line explanation. */
const ChoiceCards = ({ value, onChange, options }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(${options.length}, 1fr)`, gap: 10 }}>
    {options.map((o) => {
      const active = value === o.value;
      return (
        <button
          key={String(o.value)}
          type="button"
          onClick={() => onChange(o.value)}
          style={{
            textAlign: "left",
            padding: "12px 14px",
            borderRadius: 10,
            border: `1.5px solid ${active ? C.coral : C.border}`,
            background: active ? C.coralLight : C.white,
            cursor: "pointer",
            fontFamily: "inherit",
            transition: "all 0.15s",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: active ? C.coral : C.text }}>{o.label}</div>
          <div style={{ fontSize: 12, color: C.textLight, marginTop: 3, lineHeight: 1.4 }}>{o.hint}</div>
        </button>
      );
    })}
  </div>
);

/** A labelled field. */
const Field = ({ label, children, hint }) => (
  <div style={{ minWidth: 0 }}>
    <label
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: C.textLight,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        marginBottom: 6,
        display: "block",
      }}
    >
      {label}
    </label>
    {children}
    {hint}
  </div>
);

/** The General-tab fields, as the form holds them. Used for the initial state and the dirty check. */
const generalFormFrom = (data) => ({
  name: data.name || "",
  language: data.language || "both",
  orientation: data.orientation || "landscape",
  signageMode: data.signageMode ?? true,
  customerDisplayEnabled: data.customerDisplayEnabled ?? false,
  orderingEnabled: data.orderingEnabled ?? false,
  kitchenTicketEnabled: data.kitchenTicketEnabled ?? false,
  // 0 is meaningful here: the category stays up for as long as its products take. `|| 30` used to
  // turn a stored 0 into 30 on the next save of any other field.
  defaultCategoryDuration: data.defaultCategoryDuration ?? 0,
  // The model's and the device's own default is 2 seconds.
  defaultProductDuration: data.defaultProductDuration || 2,
  staffPin: data.staffPin || "0000",
  SpanMachineId: data.SpanMachineId || "",
});

const ScreenConfigure = () => {
  const { screenId } = useParams();
  const [activeTab, setActiveTab] = useState("general");
  const [screen, setScreen] = useState(null);
  const [isFetching, setIsFetching] = useState(true);

  // General tab
  const [generalForm, setGeneralForm] = useState({});
  // What the server holds, so the save bar can say whether anything has changed.
  const [savedForm, setSavedForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  // Terminals this screen is allowed to use: same branch, and not already taken by another screen.
  // Fetched with the screen rather than on tab entry, because the General tab is the default one.
  const [terminals, setTerminals] = useState([]);

  // Categories tab
  const [categories, setCategories] = useState([]);
  const [categoryChanges, setCategoryChanges] = useState({});

  // Products tab
  const [products, setProducts] = useState([]);
  const [productChanges, setProductChanges] = useState({});
  const [selectedCatFilter, setSelectedCatFilter] = useState("");

  // Promotions tab
  const [promotions, setPromotions] = useState([]);
  const [promoModal, setPromoModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [promoForm, setPromoForm] = useState({
    title: "", arabicTitle: "", type: "image", mediaUrl: "",
    sortOrder: 0, duration: 10, isActive: true, startDate: "", endDate: "",
  });

  useEffect(() => {
    fetchScreen();
  }, [screenId]);

  useEffect(() => {
    if (screen && activeTab === "categories") fetchCategories();
    if (screen && activeTab === "products") fetchProducts();
    if (screen && activeTab === "promotions") fetchPromotions();
  }, [screen, activeTab]);

  const fetchScreen = async ({ quiet = false } = {}) => {
    if (!quiet) setIsFetching(true);
    const data = await getScreenById(screenId);
    setScreen(data);
    if (data) {
      const form = generalFormFrom(data);
      setGeneralForm(form);
      setSavedForm(form);

      // Scoped to the screen's branch, because a terminal may only be used by devices in its own
      // branch — offering the rest would just produce a rejected save.
      if (data.branchId) {
        const list = await getSpanMachineList({ branchId: data.branchId });
        setTerminals(list || []);
      } else {
        setTerminals([]);
      }
    }
    setIsFetching(false);
  };

  const generalDirty = JSON.stringify(generalForm) !== JSON.stringify(savedForm);

  const handleSaveGeneral = async () => {
    // Caught here as well as server-side, because the cost of a bad PIN falls on someone else: it
    // saves cleanly, and is only discovered by a staff member standing at a tablet that will not
    // open, with no way to see why.
    if (!/^\d{4,8}$/.test(String(generalForm.staffPin || ""))) {
      window.alert("The staff PIN must be 4 to 8 digits.");
      return;
    }
    setIsSaving(true);
    const ok = await updateScreen(screenId, generalForm);
    setIsSaving(false);
    // Refetched quietly: the full-page loader here would throw away the scroll position.
    if (ok) fetchScreen({ quiet: true });
  };

  const handleReissueApiKey = async () => {
    const ok = window.confirm(
      "Re-issue this screen's API key?\n\nThe current key stops working immediately. The screen " +
      "picks up the new one on its next config poll (within ~30 seconds)."
    );
    if (!ok) return;
    await reissueScreenApiKey(screenId);
    fetchScreen({ quiet: true });
  };

  // Categories
  const fetchCategories = async () => {
    const data = await getScreenCategories(screenId);
    setCategories(data || []);
    setCategoryChanges({});
  };

  const updateCategoryField = (catId, field, value) => {
    setCategoryChanges((prev) => ({
      ...prev,
      [catId]: { ...(prev[catId] || {}), [field]: value },
    }));
  };

  const getCategoryValue = (cat, field) => {
    if (categoryChanges[cat.id] && categoryChanges[cat.id][field] !== undefined) {
      return categoryChanges[cat.id][field];
    }
    return cat[field];
  };

  const handleSaveCategories = async () => {
    const payload = categories.map((cat) => ({
      categoryId: cat.id,
      isVisible: getCategoryValue(cat, "isVisible"),
      sortOrder: getCategoryValue(cat, "sortOrder") || null,
      duration: getCategoryValue(cat, "duration") || null,
      displayName: getCategoryValue(cat, "displayName") || null,
      displayArabicName: getCategoryValue(cat, "displayArabicName") || null,
    }));
    await setScreenCategories(screenId, payload);
    fetchCategories();
  };

  // Products
  const fetchProducts = async () => {
    const data = await getScreenProducts(screenId, selectedCatFilter || undefined);
    setProducts(data || []);
    setProductChanges({});
  };

  const updateProductField = (prodId, field, value) => {
    setProductChanges((prev) => ({
      ...prev,
      [prodId]: { ...(prev[prodId] || {}), [field]: value },
    }));
  };

  const getProductValue = (prod, field) => {
    if (productChanges[prod.id] && productChanges[prod.id][field] !== undefined) {
      return productChanges[prod.id][field];
    }
    return prod[field];
  };

  const handleSaveProducts = async () => {
    const payload = products.map((prod) => ({
      productId: prod.id,
      isVisible: getProductValue(prod, "isVisible"),
      isFeatured: getProductValue(prod, "isFeatured"),
      sortOrder: getProductValue(prod, "sortOrder") || null,
      duration: getProductValue(prod, "duration") || null,
    }));
    await setScreenProducts(screenId, payload);
    fetchProducts();
  };

  // Promotions
  const fetchPromotions = async () => {
    const data = await getScreenPromotions(screenId);
    setPromotions(data || []);
  };

  const openPromoModal = (promo = null) => {
    if (promo) {
      setEditingPromo(promo);
      setPromoForm({
        title: promo.title || "",
        arabicTitle: promo.arabicTitle || "",
        type: promo.type || "image",
        mediaUrl: promo.mediaUrl || "",
        sortOrder: promo.sortOrder || 0,
        duration: promo.duration || 10,
        isActive: promo.isActive ?? true,
        startDate: promo.startDate ? promo.startDate.split("T")[0] : "",
        endDate: promo.endDate ? promo.endDate.split("T")[0] : "",
      });
    } else {
      setEditingPromo(null);
      setPromoForm({
        title: "", arabicTitle: "", type: "image", mediaUrl: "",
        sortOrder: 0, duration: 10, isActive: true, startDate: "", endDate: "",
      });
    }
    setPromoModal(true);
  };

  const handleSavePromo = async () => {
    const payload = {
      ...promoForm,
      startDate: promoForm.startDate || null,
      endDate: promoForm.endDate || null,
    };
    if (editingPromo) {
      await updateScreenPromotion(screenId, editingPromo.id, payload);
    } else {
      await createScreenPromotion(screenId, payload);
    }
    setPromoModal(false);
    fetchPromotions();
  };

  const handleDeletePromo = async (promoId) => {
    if (window.confirm("Delete this promotion?")) {
      await deleteScreenPromotion(screenId, promoId);
      fetchPromotions();
    }
  };

  // ---- Shared styles ----
  const inputStyle = {
    padding: "9px 12px",
    fontSize: 13,
    borderRadius: 8,
    border: `1.5px solid ${C.border}`,
    fontFamily: "inherit",
    outline: "none",
    color: C.text,
    background: C.white,
    width: "100%",
    transition: "border 0.15s",
  };

  const smallInputStyle = { ...inputStyle, padding: "6px 10px", fontSize: 12 };

  const labelStyle = {
    fontSize: 11,
    fontWeight: 700,
    color: C.textLight,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 6,
    display: "block",
  };

  const primaryBtnStyle = {
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
    border: "none",
    borderRadius: 8,
    padding: "9px 22px",
    fontWeight: 600,
    color: C.white,
    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
  };

  const secondaryBtnStyle = {
    background: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "9px 18px",
    fontWeight: 600,
    color: C.textMed,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
  };

  const StatusPill = ({ isActive }) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "4px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: isActive ? C.greenLight : C.coralLight,
        color: isActive ? C.green : C.coral,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: isActive ? C.green : C.coral }} />
      {isActive ? "Active" : "Inactive"}
    </span>
  );

  const TypeBadge = ({ type }) => {
    const colors = type === "video"
      ? { bg: C.blueLight, fg: C.blue }
      : { bg: C.purpleLight, fg: C.purple };
    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 10px",
          borderRadius: 6,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.3,
          background: colors.bg,
          color: colors.fg,
        }}
      >
        {(type || "image").toUpperCase()}
      </span>
    );
  };

  const ActionIcon = ({ icon: Icon, color, title, onClick }) => (
    <div
      onClick={onClick}
      title={title}
      style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.15s",
        border: `1px solid ${C.border}`,
        background: C.white,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = C.coralLight;
        e.currentTarget.style.borderColor = C.coral;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = C.white;
        e.currentTarget.style.borderColor = C.border;
      }}
    >
      <Icon size={15} color={color} />
    </div>
  );

  if (isFetching) {
    return (
      <Fragment>
        <Breadcrumb title="Configure" parent="Digital Signage" />
        <Container fluid>
          <style>{reportingGlobalCSS}</style>
          <div className="rpt-wrap">
            <RLoader />
          </div>
        </Container>
      </Fragment>
    );
  }

  if (!screen) {
    return (
      <Fragment>
        <Breadcrumb title="Configure" parent="Digital Signage" />
        <Container fluid>
          <style>{reportingGlobalCSS}</style>
          <div className="rpt-wrap">
            <RCard>
              <div style={{ padding: 40, textAlign: "center", color: C.textLight }}>Screen not found</div>
            </RCard>
          </div>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Breadcrumb title={`Configure: ${screen.screenCode}`} parent="Digital Signage" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          {/* Header card */}
          <RCard>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
                }}
              >
                <Settings size={22} color={C.white} />
              </div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: C.text, letterSpacing: -0.3 }}>
                  {screen.name || screen.screenCode}
                </div>
                <div style={{ fontSize: 12, color: C.textMed, marginTop: 4 }}>
                  {screen.Merchant?.name || "—"} · {screen.Branch?.name || "—"} ·{" "}
                  <code style={{ color: C.textMed }}>{screen.screenCode}</code>
                </div>
              </div>
            </div>
          </RCard>

          {/* Tab nav (underline style) */}
          <div
            style={{
              display: "flex",
              gap: 4,
              borderBottom: `2px solid ${C.borderLight}`,
              marginBottom: 20,
              overflowX: "auto",
            }}
          >
            {TABS.map(({ key, label, Icon }) => {
              const active = activeTab === key;
              return (
                <div
                  key={key}
                  onClick={() => setActiveTab(key)}
                  style={{
                    padding: "12px 20px",
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 600,
                    color: active ? C.coral : C.textMed,
                    borderBottom: `2px solid ${active ? C.coral : "transparent"}`,
                    marginBottom: -2,
                    transition: "all 0.15s",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    whiteSpace: "nowrap",
                  }}
                >
                  <Icon size={15} />
                  {label}
                </div>
              );
            })}
          </div>

          {/* GENERAL */}
          {activeTab === "general" && (() => {
            const set = (patch) => setGeneralForm((prev) => ({ ...prev, ...patch }));
            const isMenuBoard = !!generalForm.signageMode;
            const chosenTerminal = terminals.find(
              (t) => String(t.id) === String(generalForm.SpanMachineId)
            );

            // Each of these is a state where the screen would silently take no card payment, which
            // is worth saying out loud on the page that caused it.
            const terminalHint = (() => {
              if (!generalForm.SpanMachineId) {
                return <div style={hintStyle}>Add terminals under Setting → Payment Terminals.</div>;
              }
              if (chosenTerminal && !chosenTerminal.active) {
                return (
                  <div style={warnStyle}>
                    This terminal is inactive — the screen will keep taking counter-paid orders until
                    it is switched on.
                  </div>
                );
              }
              if (chosenTerminal && !chosenTerminal.hasCredential) {
                return (
                  <div style={warnStyle}>
                    This terminal has no credential yet — add it under Setting → Payment Terminals.
                  </div>
                );
              }
              if (chosenTerminal && chosenTerminal.environment !== "production") {
                return (
                  <div style={warnStyle}>
                    {chosenTerminal.environment} credential — no real money will be taken.
                  </div>
                );
              }
              return (
                <div style={hintStyle}>
                  Customers pay by card on the screen; orders reach the POS already paid.
                </div>
              );
            })();

            return (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))",
                    gap: 16,
                    alignItems: "start",
                  }}
                >
                  {/* Left column: what the screen is and what it shows. */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <Section icon={Monitor} title="Screen" subtitle="How it is named and laid out.">
                      <Field label="Screen name">
                        <input
                          type="text"
                          value={generalForm.name || ""}
                          placeholder="e.g. Lobby display"
                          onChange={(e) => set({ name: e.target.value })}
                          style={inputStyle}
                        />
                      </Field>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <Field label="Language">
                          <select
                            value={generalForm.language}
                            onChange={(e) => set({ language: e.target.value })}
                            style={inputStyle}
                          >
                            <option value="both">Both (EN/AR)</option>
                            <option value="en">English only</option>
                            <option value="ar">Arabic only</option>
                          </select>
                        </Field>
                        <Field label="Orientation">
                          <select
                            value={generalForm.orientation}
                            onChange={(e) => set({ orientation: e.target.value })}
                            style={inputStyle}
                          >
                            <option value="landscape">Landscape</option>
                            <option value="portrait">Portrait</option>
                          </select>
                        </Field>
                      </div>
                    </Section>

                    <Section icon={Layout} title="Display mode" subtitle="What customers see on the screen.">
                      {/* The device's `signageMode`. The two are exclusive on the device —
                          ordering only exists on the interactive board — so they are offered as a
                          choice rather than a checkbox that silently disables something else. */}
                      <ChoiceCards
                        value={isMenuBoard}
                        onChange={(v) => set({ signageMode: v })}
                        options={[
                          {
                            value: true,
                            label: "Menu board",
                            hint: "Rotates through categories and products on its own. Display only.",
                          },
                          {
                            value: false,
                            label: "Interactive",
                            hint: "Customers browse the menu by touch, and can order if enabled.",
                          },
                        ]}
                      />

                      {/* Rotation timing is only read by the auto-rotating player, so it is only
                          asked for there. */}
                      {isMenuBoard && (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                          <Field
                            label="Category time (s)"
                            hint={<div style={hintStyle}>0 = as long as its products take.</div>}
                          >
                            <input
                              type="number"
                              min={0}
                              value={generalForm.defaultCategoryDuration ?? 0}
                              onChange={(e) =>
                                set({ defaultCategoryDuration: Math.max(0, parseInt(e.target.value, 10) || 0) })
                              }
                              style={inputStyle}
                            />
                          </Field>
                          <Field
                            label="Product time (s)"
                            hint={<div style={hintStyle}>Per product, unless set on the product.</div>}
                          >
                            <input
                              type="number"
                              min={1}
                              value={generalForm.defaultProductDuration ?? 2}
                              onChange={(e) =>
                                set({ defaultProductDuration: Math.max(1, parseInt(e.target.value, 10) || 1) })
                              }
                              style={inputStyle}
                            />
                          </Field>
                        </div>
                      )}
                    </Section>

                    <Section icon={Airplay} title="Customer display" subtitle="Pairs with a POS over the shop's network.">
                      <Switch
                        checked={generalForm.customerDisplayEnabled}
                        onChange={(v) => set({ customerDisplayEnabled: v })}
                        label="Act as the POS customer-facing display"
                        hint="While a cashier rings up an order, the screen shows it to the customer."
                      />
                    </Section>
                  </div>

                  {/* Right column: ordering, payment and the device itself. */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <Section icon={ShoppingBag} title="Self-ordering" subtitle="Customers place their own orders.">
                      <Switch
                        checked={generalForm.orderingEnabled}
                        // Left switchable when already on, so a screen saved in the old
                        // on-but-menu-board state can still be turned off here.
                        disabled={isMenuBoard && !generalForm.orderingEnabled}
                        onChange={(v) => set({ orderingEnabled: v })}
                        label="Let customers place orders"
                        hint={
                          isMenuBoard
                            ? "Needs the Interactive display mode."
                            : "Unpaid orders are paid at the counter unless a terminal is assigned below."
                        }
                      />
                      {isMenuBoard && generalForm.orderingEnabled && (
                        <div style={{ ...warnStyle, marginTop: -6 }}>
                          Ordering is on but will not appear until the display mode is Interactive.
                        </div>
                      )}

                      {!!generalForm.orderingEnabled && (
                        <div
                          style={{
                            borderTop: `1px solid ${C.borderLight}`,
                            paddingTop: 14,
                            display: "flex",
                            flexDirection: "column",
                            gap: 14,
                          }}
                        >
                          {/* Card payment. There is no on/off switch here on purpose: a screen
                              takes card when it holds an active terminal, so assigning one IS the
                              switch and the two can never disagree. */}
                          <Field label="Payment terminal" hint={terminalHint}>
                            <select
                              value={generalForm.SpanMachineId || ""}
                              onChange={(e) => set({ SpanMachineId: e.target.value || null })}
                              style={inputStyle}
                            >
                              <option value="">None — customers pay at the counter</option>
                              {terminals
                                // A terminal already held by another screen cannot be assigned
                                // here, so it is left out rather than offered and rejected on save.
                                .filter(
                                  (t) =>
                                    !t.assignedScreen ||
                                    String(t.assignedScreen.id) === String(screen?.id)
                                )
                                .map((t) => (
                                  <option key={t.id} value={t.id}>
                                    {t.name}
                                    {t.terminalId ? ` · ${t.terminalId}` : ""}
                                    {t.active ? "" : " · inactive"}
                                    {t.hasCredential ? "" : " · no credential"}
                                  </option>
                                ))}
                            </select>
                          </Field>

                          <Switch
                            checked={generalForm.kitchenTicketEnabled}
                            onChange={(v) => set({ kitchenTicketEnabled: v })}
                            label="Print a kitchen ticket after each order"
                            hint="The customer's tax invoice always prints. This is the second, smaller kitchen slip."
                          />
                        </div>
                      )}

                      {/* A terminal left assigned while ordering is off does nothing; say so
                          rather than hiding the field that holds it. */}
                      {!generalForm.orderingEnabled && !!generalForm.SpanMachineId && (
                        <div style={warnStyle}>
                          A payment terminal is still assigned, but does nothing while self-ordering is off.
                        </div>
                      )}
                    </Section>

                    <Section icon={Lock} title="Device access" subtitle="Staff settings on the tablet itself.">
                      {/* Opens the on-device staff panel — printers, terminal, kitchen diagnostics.
                          Deliberately not a password field: it is a shared PIN staff read off this
                          page and type into a tablet, and the page is already behind a login. */}
                      <Field
                        label="Staff PIN"
                        hint={
                          <div style={hintStyle}>
                            4–8 digits. Entered after holding the gear icon on the screen for five
                            seconds. New screens ship with 0000 — change it.
                          </div>
                        }
                      >
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={8}
                          value={generalForm.staffPin ?? ""}
                          // Digits only, filtered as it is typed: the device's keypad has no
                          // letters, so a PIN with one in it could never be entered.
                          onChange={(e) => set({ staffPin: e.target.value.replace(/\D/g, "") })}
                          style={{ ...inputStyle, maxWidth: 200, fontFamily: "monospace", letterSpacing: 2 }}
                        />
                      </Field>

                      {screen?.status === "active" && (
                        <div
                          style={{
                            borderTop: `1px solid ${C.borderLight}`,
                            paddingTop: 14,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            flexWrap: "wrap",
                          }}
                        >
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>API key</div>
                            <div style={{ fontSize: 12, marginTop: 2, color: screen?.apiKeyDeliveredAt ? C.green : C.orange }}>
                              {screen?.apiKeyDeliveredAt
                                ? "Claimed by the screen."
                                : "Not yet claimed — delivered on the screen's next poll."}
                            </div>
                          </div>
                          <button onClick={handleReissueApiKey} style={secondaryBtnStyle}>
                            <Key size={14} /> Re-issue key
                          </button>
                        </div>
                      )}
                    </Section>
                  </div>
                </div>

                {/* Save bar: sticks to the bottom so it is reachable from either column. */}
                <div
                  style={{
                    position: "sticky",
                    bottom: 0,
                    zIndex: 5,
                    marginTop: 16,
                    padding: "12px 16px",
                    background: C.white,
                    border: `1px solid ${C.border}`,
                    borderRadius: 12,
                    boxShadow: "0 -4px 16px rgba(0,0,0,0.05)",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 600, color: generalDirty ? C.orange : C.textLight }}>
                    {generalDirty ? "● Unsaved changes" : "All changes saved"}
                  </span>
                  <div style={{ flex: 1 }} />
                  <button
                    onClick={() => setGeneralForm(savedForm)}
                    disabled={!generalDirty || isSaving}
                    style={{ ...secondaryBtnStyle, opacity: !generalDirty || isSaving ? 0.55 : 1 }}
                  >
                    Discard
                  </button>
                  <button
                    onClick={handleSaveGeneral}
                    disabled={!generalDirty || isSaving}
                    style={{ ...primaryBtnStyle, opacity: !generalDirty || isSaving ? 0.55 : 1 }}
                  >
                    <Save size={14} /> {isSaving ? "Saving…" : "Save settings"}
                  </button>
                </div>
              </>
            );
          })()}

          {/* CATEGORIES */}
          {activeTab === "categories" && (
            <RCard>
              {categories.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center", color: C.textLight }}>
                  No categories found for this branch.
                </div>
              ) : (
                <>
                  <div
                    style={{
                      overflowX: "auto",
                      borderRadius: 10,
                      border: `1px solid ${C.border}`,
                      marginBottom: 16,
                    }}
                  >
                    <table style={tbl.table}>
                      <thead>
                        <tr>
                          <th style={{ ...tbl.th, textAlign: "center", width: 70 }}>Visible</th>
                          <th style={tbl.th}>Category</th>
                          <th style={tbl.th}>Arabic</th>
                          <th style={tbl.th}>Display Override</th>
                          <th style={tbl.th}>Arabic Override</th>
                          <th style={{ ...tbl.th, width: 90 }}>Sort</th>
                          <th style={{ ...tbl.th, width: 100 }}>Duration (s)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categories.map((cat) => {
                          const visible = getCategoryValue(cat, "isVisible");
                          return (
                            <tr
                              key={cat.id}
                              className="rpt-row"
                              style={{ opacity: visible ? 1 : 0.55 }}
                            >
                              <td style={{ ...tbl.td, textAlign: "center" }}>
                                <input
                                  type="checkbox"
                                  checked={!!visible}
                                  onChange={(e) => updateCategoryField(cat.id, "isVisible", e.target.checked)}
                                  style={{ width: 16, height: 16, accentColor: C.coral }}
                                />
                              </td>
                              <td style={{ ...tbl.td, fontWeight: 600, color: C.text }}>{cat.name}</td>
                              <td style={{ ...tbl.td, color: C.textMed }}>{cat.arabicName || "—"}</td>
                              <td style={tbl.td}>
                                <input
                                  type="text"
                                  placeholder="Default"
                                  value={getCategoryValue(cat, "displayName") || ""}
                                  onChange={(e) =>
                                    updateCategoryField(cat.id, "displayName", e.target.value || null)
                                  }
                                  style={smallInputStyle}
                                />
                              </td>
                              <td style={tbl.td}>
                                <input
                                  type="text"
                                  placeholder="Default"
                                  value={getCategoryValue(cat, "displayArabicName") || ""}
                                  onChange={(e) =>
                                    updateCategoryField(cat.id, "displayArabicName", e.target.value || null)
                                  }
                                  style={smallInputStyle}
                                />
                              </td>
                              <td style={tbl.td}>
                                <input
                                  type="number"
                                  placeholder="Auto"
                                  value={getCategoryValue(cat, "sortOrder") ?? ""}
                                  onChange={(e) =>
                                    updateCategoryField(
                                      cat.id,
                                      "sortOrder",
                                      e.target.value ? parseInt(e.target.value) : null
                                    )
                                  }
                                  style={smallInputStyle}
                                />
                              </td>
                              <td style={tbl.td}>
                                <input
                                  type="number"
                                  placeholder="Default"
                                  value={getCategoryValue(cat, "duration") ?? ""}
                                  onChange={(e) =>
                                    updateCategoryField(
                                      cat.id,
                                      "duration",
                                      e.target.value ? parseInt(e.target.value) : null
                                    )
                                  }
                                  style={smallInputStyle}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <button onClick={handleSaveCategories} style={primaryBtnStyle}>
                    <Save size={14} /> Save Category Settings
                  </button>
                </>
              )}
            </RCard>
          )}

          {/* PRODUCTS */}
          {activeTab === "products" && (
            <RCard>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 16,
                  flexWrap: "wrap",
                }}
              >
                <label style={{ fontSize: 12, fontWeight: 700, color: C.textMed, marginBottom: 0 }}>
                  Filter by Category:
                </label>
                <select
                  value={selectedCatFilter}
                  onChange={(e) => setSelectedCatFilter(e.target.value)}
                  style={{ ...inputStyle, width: 260 }}
                >
                  <option value="">All Categories</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={fetchProducts}
                  style={{
                    padding: "9px 18px",
                    borderRadius: 8,
                    border: `1.5px solid ${C.border}`,
                    background: C.white,
                    color: C.text,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Load
                </button>
              </div>

              {products.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center", color: C.textLight }}>
                  No products found. Select a category and click Load.
                </div>
              ) : (
                <>
                  <div
                    style={{
                      overflowX: "auto",
                      borderRadius: 10,
                      border: `1px solid ${C.border}`,
                      marginBottom: 16,
                    }}
                  >
                    <table style={tbl.table}>
                      <thead>
                        <tr>
                          <th style={{ ...tbl.th, textAlign: "center", width: 70 }}>Visible</th>
                          <th style={{ ...tbl.th, textAlign: "center", width: 80 }}>Featured</th>
                          <th style={tbl.th}>Product</th>
                          <th style={tbl.th}>Category</th>
                          <th style={{ ...tbl.th, width: 100 }}>Price</th>
                          <th style={{ ...tbl.th, width: 90 }}>Sort</th>
                          <th style={{ ...tbl.th, width: 100 }}>Duration (s)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.map((prod) => {
                          const visible = getProductValue(prod, "isVisible");
                          const featured = getProductValue(prod, "isFeatured");
                          return (
                            <tr
                              key={prod.id}
                              className="rpt-row"
                              style={{ opacity: visible ? 1 : 0.55 }}
                            >
                              <td style={{ ...tbl.td, textAlign: "center" }}>
                                <input
                                  type="checkbox"
                                  checked={!!visible}
                                  onChange={(e) => updateProductField(prod.id, "isVisible", e.target.checked)}
                                  style={{ width: 16, height: 16, accentColor: C.coral }}
                                />
                              </td>
                              <td style={{ ...tbl.td, textAlign: "center" }}>
                                <input
                                  type="checkbox"
                                  checked={!!featured}
                                  onChange={(e) => updateProductField(prod.id, "isFeatured", e.target.checked)}
                                  style={{ width: 16, height: 16, accentColor: C.orange }}
                                />
                              </td>
                              <td style={{ ...tbl.td, fontWeight: 600, color: C.text }}>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                  {prod.title}
                                  {featured && <Star size={12} color={C.orange} fill={C.orange} />}
                                </span>
                              </td>
                              <td style={{ ...tbl.td, color: C.textMed }}>{prod.categoryName || "—"}</td>
                              <td style={{ ...tbl.td, color: C.text, fontWeight: 600 }}>{prod.price}</td>
                              <td style={tbl.td}>
                                <input
                                  type="number"
                                  placeholder="Auto"
                                  value={getProductValue(prod, "sortOrder") ?? ""}
                                  onChange={(e) =>
                                    updateProductField(
                                      prod.id,
                                      "sortOrder",
                                      e.target.value ? parseInt(e.target.value) : null
                                    )
                                  }
                                  style={smallInputStyle}
                                />
                              </td>
                              <td style={tbl.td}>
                                <input
                                  type="number"
                                  placeholder="Default"
                                  value={getProductValue(prod, "duration") ?? ""}
                                  onChange={(e) =>
                                    updateProductField(
                                      prod.id,
                                      "duration",
                                      e.target.value ? parseInt(e.target.value) : null
                                    )
                                  }
                                  style={smallInputStyle}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <button onClick={handleSaveProducts} style={primaryBtnStyle}>
                    <Save size={14} /> Save Product Settings
                  </button>
                </>
              )}
            </RCard>
          )}

          {/* PROMOTIONS */}
          {activeTab === "promotions" && (
            <RCard>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
                <button onClick={() => openPromoModal()} style={primaryBtnStyle}>
                  + Add Promotion
                </button>
              </div>

              {promotions.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center", color: C.textLight }}>
                  No promotions yet.
                </div>
              ) : (
                <div
                  style={{
                    overflowX: "auto",
                    borderRadius: 10,
                    border: `1px solid ${C.border}`,
                  }}
                >
                  <table style={tbl.table}>
                    <thead>
                      <tr>
                        <th style={tbl.th}>Title</th>
                        <th style={tbl.th}>Type</th>
                        <th style={tbl.th}>Duration</th>
                        <th style={tbl.th}>Status</th>
                        <th style={tbl.th}>Schedule</th>
                        <th style={{ ...tbl.th, textAlign: "center" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {promotions.map((promo) => (
                        <tr key={promo.id} className="rpt-row">
                          <td style={{ ...tbl.td, fontWeight: 600, color: C.text }}>{promo.title}</td>
                          <td style={tbl.td}>
                            <TypeBadge type={promo.type} />
                          </td>
                          <td style={{ ...tbl.td, color: C.textMed }}>{promo.duration}s</td>
                          <td style={tbl.td}>
                            <StatusPill isActive={promo.isActive} />
                          </td>
                          <td style={{ ...tbl.td, color: C.textLight, fontSize: 12 }}>
                            {promo.startDate || promo.endDate
                              ? `${promo.startDate?.split("T")[0] || "…"} → ${promo.endDate?.split("T")[0] || "…"}`
                              : "Always"}
                          </td>
                          <td style={tbl.td}>
                            <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
                              <ActionIcon
                                icon={Edit}
                                color={C.blue}
                                title="Edit"
                                onClick={() => openPromoModal(promo)}
                              />
                              <ActionIcon
                                icon={Trash2}
                                color={C.coral}
                                title="Delete"
                                onClick={() => handleDeletePromo(promo.id)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </RCard>
          )}
        </div>
      </Container>

      {/* Promotion Modal */}
      <Modal isOpen={promoModal} toggle={() => setPromoModal(false)} size="lg" centered>
        <ModalHeader toggle={() => setPromoModal(false)} style={{ borderBottom: `2px solid ${C.borderLight}` }}>
          <span style={{ fontWeight: 700, color: C.text }}>
            {editingPromo ? "Edit Promotion" : "Add Promotion"}
          </span>
        </ModalHeader>
        <ModalBody style={{ padding: 24 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 16,
            }}
          >
            <div>
              <label style={labelStyle}>Title *</label>
              <input
                type="text"
                value={promoForm.title}
                onChange={(e) => setPromoForm({ ...promoForm, title: e.target.value })}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Arabic Title</label>
              <input
                type="text"
                value={promoForm.arabicTitle}
                onChange={(e) => setPromoForm({ ...promoForm, arabicTitle: e.target.value })}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Type</label>
              <select
                value={promoForm.type}
                onChange={(e) => setPromoForm({ ...promoForm, type: e.target.value })}
                style={inputStyle}
              >
                <option value="image">Image</option>
                <option value="video">Video</option>
              </select>
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <label style={labelStyle}>Media URL *</label>
              <input
                type="text"
                value={promoForm.mediaUrl}
                placeholder="https://... or relative path"
                onChange={(e) => setPromoForm({ ...promoForm, mediaUrl: e.target.value })}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Sort Order</label>
              <input
                type="number"
                value={promoForm.sortOrder}
                onChange={(e) => setPromoForm({ ...promoForm, sortOrder: parseInt(e.target.value) || 0 })}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Duration (s)</label>
              <input
                type="number"
                value={promoForm.duration}
                onChange={(e) => setPromoForm({ ...promoForm, duration: parseInt(e.target.value) || 10 })}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Start Date</label>
              <input
                type="date"
                value={promoForm.startDate}
                onChange={(e) => setPromoForm({ ...promoForm, startDate: e.target.value })}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>End Date</label>
              <input
                type="date"
                value={promoForm.endDate}
                onChange={(e) => setPromoForm({ ...promoForm, endDate: e.target.value })}
                style={inputStyle}
              />
            </div>
          </div>
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              marginTop: 16,
              fontSize: 13,
              color: C.text,
            }}
          >
            <input
              type="checkbox"
              checked={promoForm.isActive}
              onChange={(e) => setPromoForm({ ...promoForm, isActive: e.target.checked })}
              style={{ width: 16, height: 16, accentColor: C.coral }}
            />
            Active
          </label>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPromoModal(false)}>
            Cancel
          </Button>
          <button
            onClick={handleSavePromo}
            disabled={!promoForm.title || !promoForm.mediaUrl}
            style={{
              ...primaryBtnStyle,
              opacity: !promoForm.title || !promoForm.mediaUrl ? 0.55 : 1,
              cursor: !promoForm.title || !promoForm.mediaUrl ? "not-allowed" : "pointer",
            }}
          >
            {editingPromo ? "Update" : "Create"}
          </button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default ScreenConfigure;
